import { useEffect, useMemo, useState } from 'react';
import { listIncidents } from '../api/incidents';
import { listMyAssignments } from '../api/training';
import { Incident } from '../types/incident';
import Icon, { IconName } from './Icon';

const ACTIVE = new Set(['OPEN', 'IN_PROGRESS', 'REOPENED']);
const DONE = new Set(['RESOLVED', 'CLOSED']);

type Insight = { key: string; icon: IconName; tone: string; lead: string; rest: string };

function titleCase(s: string): string {
  return s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// Derive a handful of live, human-readable insights from the incident +
// training data. Always returns at least one (the critical / all-clear line).
function buildInsights(incidents: Incident[], trainingDue: number): Insight[] {
  const out: Insight[] = [];
  const total = incidents.length;
  const critical = incidents.filter((i) => i.severity === 'CRITICAL' && ACTIVE.has(i.status)).length;
  const inProgress = incidents.filter((i) => i.status === 'IN_PROGRESS').length;
  const resolved = incidents.filter((i) => DONE.has(i.status)).length;
  const newThisWeek = incidents.filter((i) => Date.now() - new Date(i.createdAt).getTime() < 7 * 86400000).length;
  const rate = total ? Math.round((resolved / total) * 100) : 0;

  const typeCounts: Record<string, number> = {};
  incidents.forEach((i) => { if (i.type) typeCounts[i.type] = (typeCounts[i.type] || 0) + 1; });
  const topType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0];

  out.push(critical > 0
    ? { key: 'crit', icon: 'alert', tone: 'var(--accent-red)', lead: `${critical} critical ${critical === 1 ? 'incident' : 'incidents'} active`, rest: 'needs attention now' }
    : { key: 'crit', icon: 'check', tone: 'var(--success)', lead: 'Everything under control', rest: 'no critical incidents active' });

  if (total > 0)
    out.push({ key: 'rate', icon: 'check', tone: 'var(--success)', lead: `${rate}% resolved`, rest: 'of all incidents this period' });

  if (inProgress > 0)
    out.push({ key: 'prog', icon: 'clipboard', tone: 'var(--warning)', lead: `${inProgress} in progress`, rest: `being worked right now` });

  if (topType && topType[1] > 1)
    out.push({ key: 'type', icon: 'list', tone: 'var(--primary)', lead: titleCase(topType[0]), rest: `most common type · ${topType[1]} logged` });

  if (newThisWeek > 0)
    out.push({ key: 'week', icon: 'calendar', tone: 'var(--primary)', lead: `${newThisWeek} new this week`, rest: 'raised in the last 7 days' });

  if (trainingDue > 0)
    out.push({ key: 'train', icon: 'cap', tone: 'var(--warning)', lead: `${trainingDue} training due`, rest: `${trainingDue === 1 ? 'module is' : 'modules are'} waiting for you` });

  return out;
}

// Auto-rotating insight card, pinned to the bottom of the sidebar. Cycles
// through the insights on its own; dots are an optional jump. Pauses on
// hover and respects prefers-reduced-motion.
export function SidebarIncidentSummary() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [trainingDue, setTrainingDue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduce, setReduce] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      listIncidents().catch(() => [] as Incident[]),
      listMyAssignments().catch(() => []),
    ]).then(([inc, asn]) => {
      if (!alive) return;
      setIncidents(inc);
      setTrainingDue(asn.filter((a) => a.status !== 'COMPLETED').length);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => { setReduce(window.matchMedia('(prefers-reduced-motion: reduce)').matches); }, []);

  const insights = useMemo(() => buildInsights(incidents, trainingDue), [incidents, trainingDue]);

  useEffect(() => { setIdx((i) => (insights.length ? i % insights.length : 0)); }, [insights.length]);

  useEffect(() => {
    if (paused || reduce || insights.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % insights.length), 4500);
    return () => clearInterval(t);
  }, [paused, reduce, insights.length]);

  if (loading || insights.length === 0) return null;
  const cur = insights[Math.min(idx, insights.length - 1)];
  const anim = reduce ? 'none' : 'sbInsightIn 0.45s ease both';

  return (
    <div
      className="cs-summary"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span
        className="cs-summary-ic"
        key={'ic' + idx}
        style={{
          background: `color-mix(in srgb, ${cur.tone} 13%, transparent)`,
          color: cur.tone, animation: anim,
        }}
      >
        <Icon name={cur.icon} size={18} />
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div key={idx} style={{ animation: anim }}>
          <div className="cs-summary-head" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {cur.lead}
          </div>
          <div className="cs-summary-sub" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {cur.rest}
          </div>
        </div>
        {insights.length > 1 && (
          <div style={{ display: 'flex', gap: 4, marginTop: 7 }}>
            {insights.map((s, i) => (
              <button
                key={s.key}
                aria-label={`Show insight ${i + 1}`}
                onClick={() => setIdx(i)}
                style={{
                  width: i === idx ? 12 : 5, height: 5, borderRadius: 999, padding: 0,
                  border: 'none', cursor: 'pointer',
                  background: i === idx ? 'var(--primary)' : 'color-mix(in srgb, var(--fg) 18%, transparent)',
                  transition: 'width 0.25s ease, background 0.25s ease',
                }}
              />
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes sbInsightIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
}
